import { useMemo } from 'react';
import { DepartmentsQueries, DepartmentsUtils } from '@frontend/api-departments';
import { PhoneNumbersV1 } from '@frontend/api-phone-numbers';
import { formatPhoneNumberE164 } from '@frontend/phone-numbers';
import { useAppFlagStore } from '@frontend/shared';
import { LocationPhone, LocationPhonesData } from '../types';
import { formatProtobufPhone } from '../utils';
import { useInboxLocationIds } from './use-inbox-location-ids';

const emptyLocationPhonesData: LocationPhonesData = {
  locationPhonesByLocation: {},
  allLocationPhones: [],
};

type UseLocationPhonesArgs = {
  locationIds?: string[];
  filterLocationPhones?: string[];
};

type UseLocationPhonesReturn = {
  data: LocationPhonesData;
  enabledData: LocationPhonesData;
  isLimitedToDepartments: boolean;
  isLoading: boolean;
};

/**
 * Hook for fetching and managing outbound phone numbers available in the inbox.
 *
 * This hook provides a unified interface for retrieving either all sms-enabled phone numbers, or only
 * department-related phone numbers SMS numbers or general location phone numbers, depending on feature flag
 * configuration. It handles ACL filtering to ensure users only see phone numbers for the locations that they have
 * inbox permissions for.
 *
 * @param locationIds - Optional array of location IDs to filter phone numbers.
 *   If not provided, uses currently selected location IDs.
 *
 * @returns Object containing:
 *   - `data`: The location phones data
 *   - `isLimitedToDepartments`: Boolean indicating if phone numbers are limited from departments
 *     (when feature flag 'phone-numbers-name-id' is disabled for any location)
 *
 * @remarks
 * - Phone numbers are formatted to E164 standard for consistency
 * - Queries are conditionally enabled based on feature flag state and location availability
 */
export const useLocationPhones = ({
  locationIds: providedLocationIds,
  filterLocationPhones = [],
}: UseLocationPhonesArgs = {}): UseLocationPhonesReturn => {
  const { getFeatureFlagValue } = useAppFlagStore();
  const locationIds = useInboxLocationIds({ locationIds: providedLocationIds });
  const isLimitedToDepartments = !getFeatureFlagValue({
    locationIds,
    flagName: 'phone-numbers-name-id',
    strategy: 'ALL',
  });

  const locationPhonesQuery = PhoneNumbersV1.Queries.useListPhoneNumbersForLocationsQuery({
    request: {
      locationIds,
      sms: true,
      excludeTenDlc: true,
    },
    options: {
      enabled: !isLimitedToDepartments && locationIds.length > 0,
    },
  });

  const { data: outboundSmsNumbers } = PhoneNumbersV1.Queries.useListOutboundSmsNumbers(
    {
      locationIds,
    },
    {
      enabled: locationIds.length > 0 && !isLimitedToDepartments,
    }
  );

  const departmentsQueries = DepartmentsQueries.useListDefaultSMSQueries(
    locationIds.map((locationId) => ({ req: { locationId }, options: { enabled: isLimitedToDepartments } }))
  );
  const locationsWithoutDepartments = useMemo(
    () =>
      locationIds.reduce<string[]>((acc, curr) => {
        const departmentsQuery = departmentsQueries.find((q) => q.data?.locationId === curr);
        if (!departmentsQuery || (!departmentsQuery.data?.smsNumbers?.length && departmentsQuery.isFetched)) {
          acc.push(curr);
        }
        return acc;
      }, []),
    [departmentsQueries, locationIds]
  );
  const smsNumbersQueries = PhoneNumbersV1.Queries.useListSMSPhoneNumbersQueries(
    locationsWithoutDepartments.map((locationId) => ({
      request: {},
      options: {
        select: (data) => ({
          ...data,
          locationId,
          sms_numbers_response: data.sms_numbers_response?.filter((number) => !!number.active) ?? [],
        }),
      },
      httpOptions: { headers: { 'Location-Id': locationId } },
    }))
  );
  const smsNumbersQueriesJSON = locationsWithoutDepartments.length ? JSON.stringify(smsNumbersQueries) : '';
  const smsNumbersQuery = useMemo(
    () => {
      if (smsNumbersQueriesJSON === '') {
        return {};
      }

      return smsNumbersQueries.reduce<Record<string, LocationPhone[]>>((acc, curr) => {
        const locationId = curr.data?.locationId;
        const numbers = curr.data?.sms_numbers_response ?? [];
        if (numbers.length && locationId) {
          const currentEntries = acc[locationId] ?? [];
          numbers.forEach(({ number, displayName }) => {
            if (number) {
              currentEntries.push({
                locationPhone: formatPhoneNumberE164(number),
                locationId,
                displayName,
              });
            }
          }, []);
          acc[locationId] = currentEntries;
        }
        return acc;
      }, {});
    },
    // We are intentionally using `smsNumbersJSON` instead of `smsNumbersQueries` to avoid unnecessary re-renders
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [smsNumbersQueriesJSON]
  );
  const departmentsQuery = useMemo(() => {
    return DepartmentsUtils.reduceDefaultSMSQueriesToLocationIdMap(
      departmentsQueries,
      ({ smsNumbers }) => smsNumbers ?? []
    );
  }, [departmentsQueries]);

  const departmentsLocationPhoneData = useMemo<LocationPhonesData>(() => {
    if (!isLimitedToDepartments) {
      return emptyLocationPhonesData;
    }

    return Object.entries(departmentsQuery.data).reduce<LocationPhonesData>(
      (acc, [locationId, departments]) => {
        if (!locationIds.includes(locationId)) {
          return acc;
        }

        const entriesFromDepartments = departments.reduce<LocationPhone[]>((acc, department) => {
          const number = department.smsNumber?.number;
          if (!number) {
            return acc;
          }

          acc.push({
            locationPhone: formatPhoneNumberE164(number),
            locationId,
            displayName: department.name,
          });
          return acc;
        }, []);

        const entriesFromSMSNumbers = smsNumbersQuery[locationId] ?? [];
        const newEntries = [...entriesFromDepartments, ...entriesFromSMSNumbers];

        if (newEntries.length) {
          acc.locationPhonesByLocation[locationId] = newEntries;
          acc.allLocationPhones.push(...newEntries);
        }

        return acc;
      },
      { locationPhonesByLocation: {}, allLocationPhones: [] }
    );
  }, [departmentsQuery.data, isLimitedToDepartments, locationIds, smsNumbersQuery]);

  const filterLocationPhonesStr = filterLocationPhones.join(',');
  const allLocationPhonesData = useMemo<LocationPhonesData>(() => {
    // v3 - department sms numbers (deprecated)
    if (isLimitedToDepartments) {
      return emptyLocationPhonesData;
    }

    // v4 - outbound sms numbers
    return (
      locationPhonesQuery.data?.phoneNumbers?.reduce<LocationPhonesData>(
        (acc, curr) => {
          if (!locationIds.includes(curr.locationId)) {
            return acc;
          }
          const currentEntries = acc.locationPhonesByLocation[curr.locationId] ?? [];
          const locationPhone = formatProtobufPhone(curr.phoneNumber);

          if (!locationPhone || filterLocationPhonesStr.includes(locationPhone)) {
            return acc;
          }

          const newEntry: LocationPhone = {
            locationPhone,
            locationId: curr.locationId,
            displayName: curr.displayName,
            defaultSms: curr.defaultSms,
            defaultAi: curr.defaultAi,
          };

          acc.locationPhonesByLocation[curr.locationId] = [...currentEntries, newEntry];
          acc.allLocationPhones.push(newEntry);
          return acc;
        },
        { locationPhonesByLocation: {}, allLocationPhones: [] }
      ) ?? { locationPhonesByLocation: {}, allLocationPhones: [] }
    );
  }, [filterLocationPhonesStr, isLimitedToDepartments, locationIds, locationPhonesQuery.data?.phoneNumbers]);

  const enabledOutboundPhoneNumbers = useMemo<LocationPhonesData>(() => {
    // v3 - department sms numbers (deprecated)
    if (isLimitedToDepartments) return emptyLocationPhonesData;

    // v4 - outbound sms numbers
    return (outboundSmsNumbers || [])
      .map(({ locationId, outboundSmsNumbers: currentOutboundSmsNumbers }) =>
        (currentOutboundSmsNumbers || []).map((number) => ({ ...number, locationId }))
      )
      .flat()
      .reduce<LocationPhonesData>(
        (acc, curr) => {
          if (!locationIds.includes(curr.locationId)) {
            return acc;
          }

          const currentEntries = acc.locationPhonesByLocation[curr.locationId] ?? [];

          if (!curr.number || filterLocationPhonesStr.includes(curr.number)) {
            return acc;
          }

          const newEntry: LocationPhone = {
            locationPhone: curr.number,
            locationId: curr.locationId,
            displayName: curr.displayName,
            defaultSms: curr.default,
          };

          acc.locationPhonesByLocation[curr.locationId] = [...currentEntries, newEntry];
          acc.allLocationPhones.push(newEntry);
          return acc;
        },
        { locationPhonesByLocation: {}, allLocationPhones: [] }
      );
  }, [filterLocationPhonesStr, isLimitedToDepartments, locationIds, outboundSmsNumbers]);

  const data = useMemo(() => {
    return isLimitedToDepartments ? departmentsLocationPhoneData : allLocationPhonesData;
  }, [isLimitedToDepartments, departmentsLocationPhoneData, allLocationPhonesData]);

  return {
    data,
    enabledData: enabledOutboundPhoneNumbers,
    isLimitedToDepartments,
    isLoading: locationPhonesQuery.isLoading,
  };
};
